import { monotonicNow } from "../time/serverClock";
import React from "react";
import { createStartupRecoveryTracker } from "./startupRecovery";
import { runStartupTask } from "./startupTask";
import { APPLICATION_API_SCOPE, consumeApiRequestInterruption, setApiRequestsBlocked } from "../api/apiRequestGate";
import { requestSharedAppReload } from "./appReloadCoordinator";
import { getUiVersionMonitor } from "./uiVersionCheck";
import { createSharedEventSource } from "../live/sharedEventSource";
import { readStartupStatus, blocksStartup, STARTUP_WAKE_EVENT } from "./startupStatus";
import { fetchDeploymentPhase, isDeploymentActive, type DeploymentPhase } from "./deploymentStatus";
import { getAppRuntimeSnapshot, signalAppRuntimePending, signalAppRuntimeReady, signalAppRuntimeUnavailable, subscribeAppRuntime } from "./appRuntimeGate";
import { STARTUP_CHECK_TIMEOUT_MS } from "./startupTimings";

const STARTUP_DELAYED_NOTICE_MS = 30_000;
const STARTUP_GRACE_MS = 800;
const DEPLOYMENT_STATUS_TIMEOUT_MS = 3000;
const POLL_READY_INTERVAL_MS = 5000;
const POLL_RETRY_INTERVAL_MS = 1500;
const WAKE_GAP_MS = 15_000;
const WATCHDOG_INTERVAL_MS = 5000;

export type StartupStage = "loading" | "checking" | "migrating" | "unavailable" | "connection" | "session" | "deploy" | "ready";
export type StartupGateOptions = {
  endpoint?: string;
  productKey: string;
  /** Fachadapter: 401 vollständig abmelden; technische Fehler werfen, damit Shared erneut prüft. */
  reconcileSession: (signal?: AbortSignal) => Promise<void>;
};

/** Ein einziger Freigabepfad für Erststart, Wartung, Versionswechsel und Wiederanlauf. */
export function useStartupGate(options: StartupGateOptions) {
  const endpoint = options.endpoint ?? "/api/v1/system/startup-status";
  const monitor = getUiVersionMonitor({ productKey: options.productKey });
  const adapters = React.useRef(options);
  React.useLayoutEffect(() => { adapters.current = options; });
  const runtime = React.useSyncExternalStore(subscribeAppRuntime, getAppRuntimeSnapshot, getAppRuntimeSnapshot);
  const version = React.useSyncExternalStore(monitor.subscribe, monitor.getSnapshot, monitor.getSnapshot);
  const [stage, setStage] = React.useState<StartupStage>("loading");
  const [graceElapsed, setGraceElapsed] = React.useState(false);
  const [delayed, setDelayed] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const [deploymentActive, setDeploymentActive] = React.useState(false);
  const retryAction = React.useRef<() => void>(() => {});
  const waiting = runtime.phase !== "ready" || version.phase !== "aligned";

  React.useEffect(() => {
    if (!waiting) return;
    const timer = window.setTimeout(() => setDelayed(true), STARTUP_DELAYED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [waiting, retryCount]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setGraceElapsed(true), STARTUP_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let generation = 0;
    let sessionPending = true;
    let ownSignal = false;
    let polling = false;
    let pollRequested = false;
    let streamConnected = false;
    let lastWake = monotonicNow();
    let sessionAttempt: AbortController | undefined;
    let versionCheck: Promise<boolean> | undefined;
    let deployment: DeploymentPhase | null = null;
    const recovery = createStartupRecoveryTracker();
    const abort = new AbortController();
    const checkVersion = () => {
      versionCheck ??= monitor.checkStartupAlignment(undefined, () => !cancelled)
        .finally(() => { versionCheck = undefined; });
      return versionCheck;
    };
    const signal = (notify: () => void) => {
      ownSignal = true;
      try { notify(); } finally { ownSignal = false; }
    };
    const interrupt = () => {
      generation++;
      sessionAttempt?.abort();
      sessionPending = true;
      recovery.interrupt();
      monitor.setBackendAvailable(false);
    };
    const unsubscribe = subscribeAppRuntime(() => {
      if (!ownSignal && getAppRuntimeSnapshot().phase === "blocked") interrupt();
    });
    const unsubscribeVersion = monitor.subscribe(() => {
      if (monitor.getSnapshot().phase !== "aligned") setApiRequestsBlocked(APPLICATION_API_SCOPE, true);
      if (monitor.getSnapshot().phase !== "aligned" && getAppRuntimeSnapshot().phase !== "blocked") signal(signalAppRuntimePending);
    });
    signal(signalAppRuntimePending);
    monitor.setBackendAvailable(false);

    async function poll() {
      if (cancelled) return;
      if (polling) {
        pollRequested = true;
        return;
      }
      polling = true;
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
      const currentGeneration = generation;
      const isCurrent = () => !cancelled && generation === currentGeneration;
      try {
        deployment = await fetchDeploymentPhase(AbortSignal.any([abort.signal, AbortSignal.timeout(DEPLOYMENT_STATUS_TIMEOUT_MS)]), deployment);
        if (!isCurrent()) return;
        setDeploymentActive(isDeploymentActive(deployment));
        if (isDeploymentActive(deployment)) {
          interrupt();
          setStage("deploy");
          signal(() => signalAppRuntimeUnavailable("updating"));
          return;
        }
        const response = await fetch(endpoint, { cache: "no-store", signal: AbortSignal.any([abort.signal, AbortSignal.timeout(STARTUP_CHECK_TIMEOUT_MS)]) });
        if (!isCurrent()) return;
        if (!response.ok) throw new Error("Startup status unavailable");
        const status = readStartupStatus(await response.json());
        if (!isCurrent()) return;
        if (blocksStartup(status)) {
          interrupt();
          const unavailable = status.status === "failed";
          setStage(unavailable ? "unavailable" : status.migrationPhase === "migrating" ? "migrating" : "checking");
          signal(() => signalAppRuntimeUnavailable(unavailable ? "unavailable" : "updating"));
          return;
        }
        // Migrations-/Schemafehler bleiben wie im Backend-Gate administrative Befunde.
        if (recovery.ready(status.startedAt)) sessionPending = true;
        // Ein bestätigter neuer Build muss auch vor Abschluss des Sitzungsabrufs laden können.
        if (sessionPending) void checkVersion();
        if (sessionPending) {
          setStage("session");
          // Eine bestehende Ausfallsperre erst nach abgeschlossenem Sitzungsabgleich
          // lösen, damit ein bereits wartender Versions-Reload nicht vorzeitig startet.
          if (getAppRuntimeSnapshot().phase !== "blocked") signal(signalAppRuntimePending);
          monitor.setBackendAvailable(false);
          sessionAttempt = new AbortController();
          const sessionSignal = AbortSignal.any([abort.signal, sessionAttempt.signal, AbortSignal.timeout(STARTUP_CHECK_TIMEOUT_MS)]);
          await runStartupTask(signal => adapters.current.reconcileSession(signal), sessionSignal);
          if (!isCurrent()) return;
          sessionPending = false;
        }
        monitor.setBackendAvailable(true);
        if (getAppRuntimeSnapshot().phase === "blocked") signal(signalAppRuntimePending);
        const aligned = await monitor.checkStartupAlignment(monitor.readServerBuild(response), isCurrent);
        if (!isCurrent()) return;
        if (!aligned) {
          setStage(monitor.getSnapshot().phase === "checking" ? "connection" : "deploy");
          signal(signalAppRuntimePending);
          return;
        }
        setStage("ready");
        setDelayed(false);
        signal(signalAppRuntimeReady);
        // Abgebrochene Flächenabrufe werden nicht einzeln nachgespielt. Nach dem
        // vollständigen Sitzungs-/Versionsabgleich entsteht einmal ein frischer Aufbau.
        // Der Backend-Startschlüssel verhindert Wiederanlaufschleifen bei dauerhaften Fehlern.
        if (consumeApiRequestInterruption(APPLICATION_API_SCOPE))
          requestSharedAppReload({ productKey: adapters.current.productKey, reason: "startup-recovered", dedupeKey: status.startedAt ?? "runtime" });
      } catch {
        if (cancelled) return;
        interrupt();
        // A timeout or session error does not prove the service is down.
        setStage("connection");
        signal(() => signalAppRuntimeUnavailable("unavailable"));
        // Die statische UI kann schon aktualisiert sein, während die API neu startet.
        // Nur beobachten: Backend und Sitzung bleiben bis zum regulären Abgleich gesperrt.
        const interruptedGeneration = generation;
        await monitor.checkStartupAlignment(undefined, () => !cancelled && generation === interruptedGeneration);
      } finally {
        sessionAttempt = undefined;
        polling = false;
        if (!cancelled) {
          if (pollRequested) {
            pollRequested = false;
            void poll();
          } else if (!streamConnected || isDeploymentActive(deployment)) {
            timer = window.setTimeout(() => void poll(), getAppRuntimeSnapshot().phase === "ready" ? POLL_READY_INTERVAL_MS : POLL_RETRY_INTERVAL_MS);
          }
        }
      }
    }
    // EventSource verbindet nach Backend-Neustarts selbst wieder. Netzwerkereignisse
    // stoßen auch im Hintergrund an; Timer bleiben Rückfall bei älteren Backends/Proxys.
    let wakeStream: ReturnType<typeof createSharedEventSource>;
    const connect = () => {
      wakeStream?.close();
      streamConnected = false;
      lastWake = monotonicNow();
      wakeStream = createSharedEventSource(`${endpoint}/events`);
      const stream = wakeStream;
      wakeStream?.addEventListener("open", () => {
        if (cancelled || wakeStream !== stream) return;
        streamConnected = true;
        if (timer !== undefined) window.clearTimeout(timer);
        timer = undefined;
      });
      wakeStream?.addEventListener("error", () => {
        if (cancelled || wakeStream !== stream) return;
        streamConnected = false;
        if (polling) void checkVersion();
        // The worker detected a real transport change; do not depend on a throttled window timer.
        void poll();
        if (!cancelled && !polling && timer === undefined) timer = window.setTimeout(() => void poll(), POLL_RETRY_INTERVAL_MS);
      });
      wakeStream?.addEventListener(STARTUP_WAKE_EVENT, () => {
        if (cancelled || wakeStream !== stream) return;
        lastWake = monotonicNow();
        // Die Versionsprüfung darf nicht hinter einem hängenden Sitzungsabruf warten.
        if (polling) void checkVersion();
        void poll();
      });
    };
    connect();
    const watchdog = window.setInterval(() => {
      if (polling) void checkVersion();
      if (monotonicNow() - lastWake >= WAKE_GAP_MS) {
        connect();
        void poll();
      }
    }, WATCHDOG_INTERVAL_MS);
    retryAction.current = () => {
      interrupt();
      connect();
      void checkVersion();
      void poll();
    };
    void poll();
    return () => {
      cancelled = true;
      wakeStream?.close();
      abort.abort();
      sessionAttempt?.abort();
      window.clearInterval(watchdog);
      retryAction.current = () => {};
      unsubscribe();
      unsubscribeVersion();
      monitor.setBackendAvailable(false);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [endpoint, monitor]);

  const visibleStage: StartupStage = deploymentActive ? "deploy"
    : runtime.phase === "ready" && version.phase === "aligned" ? "ready"
    : (version.phase === "updating" || version.phase === "manual") && stage !== "unavailable" ? "deploy"
    : stage === "ready" ? (runtime.blockKind === "unavailable" ? "unavailable" : "loading") : stage;
  const retry = () => { setDelayed(false); setRetryCount(count => count + 1); retryAction.current(); };
  return { stage: visibleStage, unavailable: !deploymentActive && runtime.phase === "blocked" && runtime.blockKind === "unavailable", delayed: waiting && delayed, retry, manualReload: !deploymentActive && version.phase === "manual", visible: visibleStage !== "ready" && (runtime.recovering || version.phase !== "aligned" || graceElapsed) };
}
